import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Flame } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PropheticFocus = () => {
  const [focus, setFocus] = useState(null);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  useEffect(() => {
    const fetchFocus = async () => {
      try {
        const res = await axios.get(`${API}/prophetic-focus`);
        setFocus(res.data);
      } catch (err) {
        console.error("Failed to fetch prophetic focus:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFocus();
  }, []);

  if (loading || !focus) return null;

  return (
    <section
      id="prophetic-focus"
      ref={ref}
      data-testid="prophetic-focus-section"
      className="py-16 md:py-24 bg-white relative overflow-hidden"
    >
      {/* Top & bottom accent lines */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 via-amber-500 to-red-600" />
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 via-amber-500 to-red-600" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Flame size={22} className="text-red-500" />
            <span className="font-accent text-2xl text-amber-600">{focus.month}</span>
            <Flame size={22} className="text-red-500" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-stone-900">
            Prophetic <span className="text-red-500">Focus</span>
          </h2>
          {focus.description && (
            <p className="text-base md:text-lg text-stone-500 mt-4 max-w-2xl mx-auto">
              {focus.description}
            </p>
          )}
        </motion.div>

        {/* Full-width image - no curves, sharp edges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="w-full"
          data-testid="prophetic-focus-image-container"
        >
          <img
            src={focus.image_url}
            alt={focus.title}
            className="w-full h-auto object-contain rounded-[2.5rem] shadow-[0_20px_60px_rgb(0,0,0,0.1)]"
            data-testid="prophetic-focus-image"
          />
        </motion.div>
      </div>
    </section>
  );
};
